import React from 'react'
import { cn } from '@merels/ui'
import { useFocusNavigationDep, useControllerInputDep } from '../../../providers/FocusNavigationProvider/FocusNavigationProvider'
import { useListNavigation } from '../../../../hooks/useListNavigation'

interface ListDetailViewProps<T> {
  items: T[]
  activeItem: T | null
  onBack?: () => void
  renderListItem: (item: T, index: number, registerRef: (el: HTMLElement | null) => void) => React.ReactNode
  renderDetails: (item: T) => React.ReactNode
  listHeader?: React.ReactNode
  loading?: boolean
  loadingMessage?: string
  errorMessage?: string | null
  emptyMessage?: string
  className?: string
  listClassName?: string
}

export function ListDetailView<T>({
  items,
  activeItem,
  onBack,
  renderListItem,
  renderDetails,
  listHeader,
  loading = false,
  loadingMessage = 'Loading...',
  errorMessage = null,
  emptyMessage = 'No items found.',
  className,
  listClassName,
}: ListDetailViewProps<T>) {
  
  const focusNavigation = useFocusNavigationDep()
  const controllerInput = useControllerInputDep()
  const isFocusNavActive = focusNavigation.isActive.value

  const listContainerRef = React.useRef<HTMLDivElement | null>(null)
  const prevActiveItemRef = React.useRef<T | null>(activeItem)

  // Track all focusable elements inside the list view container
  const [navElements, setNavElements] = React.useState<HTMLElement[]>([])

  const updateNavElements = React.useCallback(() => {
    if (!listContainerRef.current || activeItem) {
      setNavElements([])
      return
    }
    // Query all focusable elements within list view
    const elements = Array.from(
      listContainerRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [data-list-item="true"], [tabindex="0"]'
      )
    ) as HTMLElement[]
    setNavElements(elements)
  }, [activeItem])

  React.useEffect(() => {
    updateNavElements()
    // Observe DOM changes to dynamically update focusable elements list
    const observer = new MutationObserver(updateNavElements)
    if (listContainerRef.current) {
      observer.observe(listContainerRef.current, { childList: true, subtree: true })
    }
    return () => observer.disconnect()
  }, [items, activeItem, updateNavElements])

  // Escape listener to go back using controller/keyboard events
  React.useEffect(() => {
    if (!activeItem || !isFocusNavActive || focusNavigation.focusArea.value !== 'sidebar') return

    const unsubscribe = controllerInput.subscribe((event) => {
      if (event.type === 'cancel' || event.type === 'exit') {
        onBack?.()
      }
    })
    return unsubscribe
  }, [activeItem, isFocusNavActive, focusNavigation, controllerInput, onBack])

  // Focus navigation setup for all interactive elements in list view
  const listNav = useListNavigation(
    navElements,
    React.useCallback((el) => {
      el.click()
    }, []),
    !activeItem && isFocusNavActive && focusNavigation.focusArea.value === 'sidebar',
    undefined,
    false // skipFocusCheck=false since header inputs are now registered in navElements
  )

  // Register dynamic elements in listNav
  React.useEffect(() => {
    navElements.forEach((el, index) => {
      listNav.registerItem(index, el)
    })
  }, [navElements, listNav])

  React.useEffect(() => {
    if (prevActiveItemRef.current && !activeItem) {
      setTimeout(() => {
        const firstItem = listContainerRef.current?.querySelector('[data-list-item="true"]') as HTMLElement | null
        firstItem?.focus()
      }, 0)
    }
    prevActiveItemRef.current = activeItem
  }, [activeItem])

  const handleDetailKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      onBack?.()
    }
  }

  const handleListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const active = document.activeElement as HTMLElement | null
    if (!active || !e.currentTarget.contains(active)) return

    const listItems = Array.from(
      e.currentTarget.querySelectorAll('[data-list-item="true"]')
    ) as HTMLElement[]
    
    if (listItems.length === 0) return
    const activeItemEl = active.closest('[data-list-item="true"]') as HTMLElement | null
    const idx = activeItemEl ? listItems.indexOf(activeItemEl) : -1

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const nextIdx = idx === -1 ? 0 : (idx + 1) % listItems.length
      listItems[nextIdx].focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prevIdx = idx === -1 ? listItems.length - 1 : (idx - 1 + listItems.length) % listItems.length
      listItems[prevIdx].focus()
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (active.tagName === 'BUTTON' || active.tagName === 'INPUT' || active.tagName === 'SELECT') {
        return
      }
      e.preventDefault()
      active.click()
    }
  }

  if (activeItem) {
    return (
      <div 
        onKeyDown={handleDetailKeyDown}
        className={cn("flex flex-col gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300", className)}
      >
        {renderDetails(activeItem)}
      </div>
    )
  }

  return (
    <div 
      ref={listContainerRef}
      onKeyDown={handleListKeyDown}
      className={cn("flex flex-col gap-4 animate-in fade-in duration-200", className)}
    >
      {listHeader}

      {errorMessage && (
        <div className="text-xs text-danger bg-danger-bg/10 border border-danger-bg/20 p-2.5 rounded">
          {errorMessage}
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="text-xs text-muted animate-pulse">{loadingMessage}</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center border border-dashed border-basic rounded-md p-6 text-center text-muted gap-2">
          <span className="text-xs">{emptyMessage}</span>
        </div>
      ) : (
        <div className={cn("flex flex-col gap-2 overflow-y-auto pr-1", listClassName)}>
          {items.map((item, index) => 
            renderListItem(item, index, () => {})
          )}
        </div>
      )}
    </div>
  )
}
